// @flow
import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Chip,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { purple } from '@fortress-technology-solutions/fortress-component-library/design';
import { UnitAccessibilityBadge } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { useFetchHouseholdUnitAccessibility } from './hooks';
import messages from './messages';

type Props = {
  organizationId: string,
  propertyId: string,
  householdId: string,
  enabled?: boolean,
  inline?: boolean,
  alignEnd?: boolean,
};

// Same placeholder the rest of the app uses for an unavailable value.
const NOT_APPLICABLE = '---';

// Typography renders a <span> for every variant, so a label and its value would
// run together on one line ("ADAYes"). component="div" puts each on its own,
// giving the label-above-value stack the profile cards already use.
const LABEL_PROPS = {
  component: 'div',
  fontWeight: 'bold',
  variant: 'inputLabel',
};

// The four type names on one line are the longest thing this block renders, and
// the profile cards narrow to a single column on small screens. Wrapping is
// explicit so a long list reflows under itself instead of pushing the card wide
// and squeezing the unit number it sits beside.
const VALUE_PROPS = {
  component: 'div',
  sx: { overflowWrap: 'anywhere' },
};

// The unit number and floor plan beside this are plain body text and a link, so
// a pill carries the emphasis rather than a bigger or heavier font — which is
// what the design does, and what keeps the two from sharing a treatment.
// Colours are the library's purple token, the same values the design names, so
// they are not re-typed here as hex. A unit that is not accessible gets the
// Chip's default neutral colours: same shape, no emphasis.
const ACCESSIBLE_CHIP_SX = {
  color: purple.main,
  backgroundColor: purple.lighter,
  borderColor: purple.main,
};

// Box measurements read off the design: 26px tall, 6/10 padding, 100px radius,
// 11px bold text. The design's Inter is deliberately not copied — the app never
// loads it, so naming it here would fall back to a system font and look worse
// than the theme's Roboto. Its 50% letter-spacing is not copied either: at 11px
// that is 5.5px a character, which does not match the ~21px the design's own
// glyphs occupy, so the value is a display artefact rather than a spec.
const CHIP_BOX_SX = {
  height: '26px',
  borderRadius: '100px',
  '& .MuiChip-label': {
    padding: '6px 10px',
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: 1,
  },
};

const CHIP_PROPS = { size: 'small', variant: 'outlined' };

// Steps on the theme's spacing scale, not hand-picked pixels: one step between
// a label and its value, two between the two pairs, so changing the scale
// centrally moves this block with it.
const LABEL_VALUE_SPACING = 1;
const BLOCK_SPACING = 2;

/**
 * ADA status and accessibility types of the unit a household is in, or the one
 * assigned to it before move-in. Rendered on the applicant and the current
 * resident profile, next to the unit information.
 *
 * Renders nothing at all when there is nothing to show. That decision is the
 * server's: the feature flag being off, a household with no unit, and a prior
 * resident all come back as an empty object. The check is on `isADA` being
 * present rather than on truthiness, because a unit that is NOT accessible must
 * still say so — "No" is a value, not an absence.
 *
 * Two shapes. The default spells everything out as text — the label, the
 * accessible state, and every applicable type — so nothing is carried by colour
 * or by a hover alone. `inline` draws the design's pill and one icon per type
 * instead, which product chose for the profile screens; there the type name
 * lives in a tooltip and so is reachable by pointer only.
 */
const UnitAccessibilityInfo = ({
  organizationId,
  propertyId,
  householdId,
  enabled = true,
  inline = false,
  alignEnd = false,
}: Props) => {
  const { data } = useFetchHouseholdUnitAccessibility(
    organizationId,
    propertyId,
    householdId,
    enabled,
  );

  if (data?.isADA === undefined) {
    return null;
  }

  // The API already sorts these, but the order is part of the requirement, so
  // it is not left to the server to be the only guarantee. Joined into one line
  // rather than listed, matching the work order form and the management tables.
  const types = [...(data.accessibilityTypes ?? [])]
    .sort((a, b) => a.localeCompare(b))
    .join(', ');

  // Inline sits on the unit number's own row, as the design draws it: the ADA
  // pill, then one icon per type, each naming its type on hover. Product chose
  // this over spelling the names out (Damitra and Shaina, 18 Aug), so the type
  // name is reachable by pointer only.
  if (inline) {
    const badge = (
      <UnitAccessibilityBadge
        isADA={data.isADA}
        accessibilityTypes={data.accessibilityTypes}
      />
    );
    // The pushed-right wrapper belongs to the badge rather than to the caller,
    // so a row that has nothing to show is left with no element at all — an
    // empty one would still draw its share of the row's column gap.
    return alignEnd ? (
      <Box marginLeft="auto" display="flex" alignItems="center">
        {badge}
      </Box>
    ) : (
      badge
    );
  }

  return (
    <Stack spacing={BLOCK_SPACING} data-test="unit-accessibility">
      <Stack spacing={LABEL_VALUE_SPACING} alignItems="flex-start">
        <Typography {...LABEL_PROPS}>
          <FormattedMessage {...messages.ada} />
        </Typography>
        <Chip
          {...CHIP_PROPS}
          sx={{ ...CHIP_BOX_SX, ...(data.isADA ? ACCESSIBLE_CHIP_SX : {}) }}
          data-test="unit-accessibility-ada"
          label={
            data.isADA ? (
              <FormattedMessage {...messages.yes} />
            ) : (
              <FormattedMessage {...messages.no} />
            )
          }
        />
      </Stack>
      <Stack spacing={LABEL_VALUE_SPACING}>
        <Typography {...LABEL_PROPS}>
          <FormattedMessage {...messages.accessibilityType} />
        </Typography>
        <Typography {...VALUE_PROPS} data-test="unit-accessibility-types">
          {types || NOT_APPLICABLE}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default UnitAccessibilityInfo;
